import { MessageResponseDto } from "@api/api";
import { useQyUploadEmailImage } from "@factory/query/birthday.query";
import classNames from "classnames";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import { KeyboardEventHandler, useCallback, useRef } from "react";
import { Controller, FieldValues } from "react-hook-form";

import { FormControllerProp } from "../form-controller-prop.interface";
import FormError from "../form-error/form-error";

export type EditorControlType = "text" | "digit" | "password";

interface EditorControlProps<FieldsType extends FieldValues>
  extends FormControllerProp<FieldsType> {
  label?: string;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  type?: EditorControlType;
  disabled?: boolean;
  hint?: string;
  iconRight?: string;
  iconRightAction?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement> | undefined;
}

export function EditorControl<FieldsType extends FieldValues>({
  rules,
  label,
  control,
  name,
  placeholder,
  className,
  containerClassName,
  disabled,
  hint,
  iconRight,
  iconRightAction,
  onKeyDown,
}: EditorControlProps<FieldsType>) {
  const editorRef = useRef<Editor>(null);
  const onChangeRef = useRef<(val: string) => void>();

  const iconRightClass = iconRight ? "p-input-icon-right w-full" : "";
  const iconRightElement = iconRight ? (
    <i
      className={classNames(
        "pi",
        iconRight,
        iconRightAction ? "cursor-pointer pl-4" : ""
      )}
      onClick={() => {
        if (!iconRightAction) {
          return;
        }
        iconRightAction();
      }}
    />
  ) : null;

  const { mutate: uploadImage } = useQyUploadEmailImage();

  const handleEditorLoad = useCallback(
    (quill: any) => {
      const toolbar = quill.getModule("toolbar");
      if (!toolbar) return;

      const uploadAndInsert = (file: File) => {
        const range = quill.getSelection(true);

        uploadImage(
          { file },
          {
            onSuccess: (response: MessageResponseDto) => {
              const url = (response?.data as any)?.url;
              if (!url) {
                console.error("Upload failed: no URL returned", response);
                return;
              }
              quill.insertEmbed(range.index, "image", url, "user");
              quill.setSelection(range.index + 1, 0, "user");
              onChangeRef.current?.(quill.root.innerHTML);
            },
            onError: (err: unknown) => {
              console.error("Image upload failed", err);
            },
          }
        );
      };

      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
        input.onchange = () => {
          const file = input.files?.[0];
          if (file) uploadAndInsert(file);
        };
      });
    },
    [uploadImage]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        onChangeRef.current = field.onChange;

        return (
          <div className={classNames("field mt-5", containerClassName)}>
            <label className="text-xs text-gray-500" htmlFor={field.name}>
              {label}
            </label>
            <span className={classNames("p-float-label", iconRightClass)}>
              {iconRightElement}
              <Editor
                ref={editorRef}
                id={field.name}
                value={field.value}
                placeholder={placeholder}
                className={classNames(
                  { "p-invalid": fieldState.error },
                  className,
                  iconRight ? "pl-10" : ""
                )}
                onTextChange={(e: EditorTextChangeEvent) =>
                  field.onChange(e.htmlValue)
                }
                onLoad={handleEditorLoad}
                style={{ height: "320px" }}
                disabled={disabled}
                onKeyDown={onKeyDown}
              />
            </span>
            {hint && <small className="text-gray-400 mb-1">{hint}</small>}
            {fieldState.error && <FormError error={fieldState.error} />}
          </div>
        );
      }}
    />
  );
}

export default EditorControl;
